import { APICompsSchema } from "@unit/apis";
import { atom, useAtom, useAtomValue } from "jotai";

import { authUserAtom } from "@/global-state/jotai-atom";

export type AreaFilterMode = "NONE" | "INFLUENCER_AREAS" | "SELECTED_OTHER_AREAS";
type StatusKey = "NONE" | "ALL" | "AVAILABLE";
type OrderKey = "NONE" | "NEWEST" | "NEAR_DEADLINE";

type TicketListDisplayObject = {
  displayMode: "TICKETS" | "SPOTLIGHTS";
  ticketType: APICompsSchema["TicketTypeEnum"];
  areaFilter: AreaFilter;
  ticketFilter: TicketFilter;
};

/**
 * Note: "extra"について
 * チケット取得時にAPIのリクエストパラメータには使用しないが
 * フロントエンドでチケットリストの表示制御をする為に使用するプロパティ
 */
type FilterCondition = {
  keyword?: string;
  type: APICompsSchema["TicketTypeEnum"];
  areaId?: string;
  snsAccount?: string;
  genreId?: string;
  spotlightId?: string;
  sortBy: string;
  extra: {
    onlyAvailable: boolean;
    nearDeadline: boolean;
  };
};

export type AreaFilter = {
  mode: AreaFilterMode;
  prefectures: APICompsSchema["PrefectureEnum"][];
  areas: APICompsSchema["FlatAreaObject"][];
};

type TicketFilter = {
  prMethodTypes: APICompsSchema["PRMethodTypeEnum"][];
  genres: APICompsSchema["FlatGenreObject"][];
  spotlights: APICompsSchema["FlatSpotlightObject"][];
  status: { key: StatusKey; label: string; description?: string };
  order: { key: OrderKey; label: string; description?: string };
};

export const ticketStatusObjects: { key: StatusKey; label: string; description?: string }[] = [
  { key: "ALL", label: "設定しない", description: "(すべて表示)" } as const,
  { key: "AVAILABLE", label: "募集中のみ表示" } as const,
];

export const ticketOrderObjects: { key: OrderKey; label: string; description?: string }[] = [
  { key: "NEWEST", label: "設定しない", description: "(新着順)" } as const,
  { key: "NEAR_DEADLINE", label: "締切間近" } as const,
];

const initTicketListDisplay = {
  displayMode: "TICKETS",
  ticketType: "visit",
  areaFilter: {
    mode: "NONE",
    prefectures: [],
    areas: [],
  },
  ticketFilter: {
    prMethodTypes: [],
    genres: [],
    spotlights: [],
    status: ticketStatusObjects[0],
    order: ticketOrderObjects[0],
  },
} as TicketListDisplayObject;

/** ホーム（チケット一覧）画面の表示制御用の状態管理 */
const editingTicketListDisplayAtom = atom<TicketListDisplayObject>({ ...initTicketListDisplay });

const currentTicketListDisplayAtom = atom<TicketListDisplayObject>({ ...initTicketListDisplay });

/** ホーム（チケット一覧）画面のフィルタ条件の状態管理 */
const ticketFilterConditionAtom = atom<FilterCondition>({
  keyword: undefined,
  type: "visit",
  areaId: undefined,
  snsAccount: undefined,
  genreId: undefined,
  spotlightId: undefined,
  sortBy: "updatedAt-desc",
  extra: {
    onlyAvailable: false,
    nearDeadline: false,
  },
});

export const useTicketListDisplay = () => {
  const authUser = useAtomValue(authUserAtom);
  const [editingDisplay, setEditingDisplay] = useAtom(editingTicketListDisplayAtom);
  const [currentDisplay, setCurrentDisplay] = useAtom(currentTicketListDisplayAtom);
  const [ticketFilterCondition, setTicketFilterCondition] = useAtom(ticketFilterConditionAtom);
  const influencerArea = authUser?.areas || [];

  /** Display Modeの判定 */
  const isSpotlightsMode = currentDisplay.displayMode === "SPOTLIGHTS";
  /** フィルター条件の設定数 */
  const filterCount: number =
    Object.keys(currentDisplay).length -
    countTrueProperties(compareTicketFilter(currentDisplay.ticketFilter, initTicketListDisplay.ticketFilter)) +
    (currentDisplay.ticketFilter.spotlights.length > 0 ? 1 : 0) +
    (currentDisplay.ticketFilter.order.key !== "NEWEST" ? 1 : 0);

  /** 検索ボタン活性化の条件 */
  const isSearchable =
    editingDisplay.areaFilter.mode === "NONE" ||
    editingDisplay.areaFilter.mode === "INFLUENCER_AREAS" ||
    editingDisplay.areaFilter.areas.length > 0;

  const toggleDisplayMode = (withSync: boolean = false) => {
    const newDisplayMode = editingDisplay.displayMode === "TICKETS" ? "SPOTLIGHTS" : "TICKETS";
    const newFilterCondition: TicketListDisplayObject =
      newDisplayMode === "SPOTLIGHTS"
        ? { ...editingDisplay, displayMode: newDisplayMode }
        : {
            ...editingDisplay,
            ticketFilter: { ...editingDisplay.ticketFilter, spotlights: [] },
            displayMode: newDisplayMode,
          };
    setEditingDisplay(newFilterCondition);

    if (withSync) {
      updateTicketFilterCondition(newFilterCondition);
    }
  };

  const toggleTicketType = (withSync: boolean = false) => {
    const newTicketType: APICompsSchema["TicketTypeEnum"] = editingDisplay.ticketType === "visit" ? "order" : "visit";
    setEditingDisplay({ ...editingDisplay, ticketType: newTicketType });
    if (withSync) {
      updateTicketFilterCondition({ ...editingDisplay, ticketType: newTicketType });
    }
  };

  const setKeywordSearch = (value: string) => {
    setTicketFilterCondition({ ...ticketFilterCondition, keyword: value });
  };

  const setAreaFilter = (newAreaFilter: AreaFilter, withSync: boolean = false) => {
    setEditingDisplay({ ...editingDisplay, areaFilter: newAreaFilter });
    if (withSync) {
      updateTicketFilterCondition({ ...editingDisplay, areaFilter: newAreaFilter });
    }
  };

  const setTicketFilter = (newTicketFilter: TicketFilter, withSync: boolean = false) => {
    // Note: 特集が設定されている場合、displayModeはSPOTLIGHTS"
    const newFilterCondition: TicketListDisplayObject = newTicketFilter.spotlights.length
      ? { ...editingDisplay, ticketFilter: newTicketFilter, displayMode: "SPOTLIGHTS" }
      : { ...editingDisplay, ticketFilter: newTicketFilter };
    setEditingDisplay(newFilterCondition);

    if (withSync) {
      updateTicketFilterCondition(newFilterCondition);
    }
  };

  const resetEditingDisplay = () => {
    setEditingDisplay(currentDisplay);
  };

  const clearEditingDisplay = (target: ("Area" | "Ticket" | "Type")[]) => {
    let clearedDisplay = { ...initTicketListDisplay };
    if (!target.includes("Area")) {
      clearedDisplay = { ...clearedDisplay, areaFilter: editingDisplay.areaFilter };
    }
    if (!target.includes("Ticket")) {
      clearedDisplay = { ...clearedDisplay, ticketFilter: editingDisplay.ticketFilter };
    }
    if (!target.includes("Type")) {
      clearedDisplay = { ...clearedDisplay, ticketType: editingDisplay.ticketType };
    }
    setEditingDisplay({ ...clearedDisplay });
  };

  const updateTicketFilterCondition = (ticketListDisplay?: TicketListDisplayObject) => {
    const newDisplay = ticketListDisplay || editingDisplay;
    const newAreas = newDisplay.areaFilter.mode === "INFLUENCER_AREAS" ? influencerArea : newDisplay.areaFilter.areas;

    // Note: editingDisplayとcurrentDisplayを同期
    setCurrentDisplay(newDisplay);
    // Note: editingDisplayで設定されている画面表示をフィルタ条件に反映
    setTimeout(
      () =>
        setTicketFilterCondition({
          ...ticketFilterCondition,
          type: newDisplay.ticketType,
          areaId: newAreas.map((area) => area.id).join(","),
          snsAccount: newDisplay.ticketFilter.prMethodTypes.join(","),
          genreId: newDisplay.ticketFilter.genres.map((genre) => genre.id).join(","),
          spotlightId: newDisplay.ticketFilter.spotlights[0]?.id,
          sortBy: newDisplay.ticketFilter.order.key === "NEWEST" ? "updatedAt-desc" : "entryEndDate-asc",
          extra: {
            onlyAvailable: newDisplay.ticketFilter.status.key === "AVAILABLE",
            nearDeadline: newDisplay.ticketFilter.order.key === "NEAR_DEADLINE",
          },
        }),
      0,
    );
  };

  return {
    /* TicketListDisplay */
    // Properties
    editingDisplay,
    currentDisplay,
    // Computed Properties
    isSpotlightsMode,
    filterCount,
    isSearchable,
    // Actions
    toggleDisplayMode,
    toggleTicketType,
    setKeywordSearch,
    setAreaFilter,
    setTicketFilter,
    resetEditingDisplay,
    clearEditingDisplay,
    /* TicketFilterCondition */
    // Properties
    ticketFilterCondition,
    // Actions
    updateTicketFilterCondition,
  };
};

/**
 * 引数で渡したTicketFilterを比較してプロパティが等しければtrueを返す
 * @param a
 * @param b
 */
const compareTicketFilter = (a: TicketFilter, b: TicketFilter) => {
  const genreIdsA = a.genres.map((genre) => genre.id);
  const genreIdsB = b.genres.map((genre) => genre.id);
  return {
    prMethodTypes: JSON.stringify(a.prMethodTypes.concat().sort()) === JSON.stringify(b.prMethodTypes.concat().sort()),
    genres: JSON.stringify(genreIdsA.sort()) === JSON.stringify(genreIdsB.sort()),
    status: a.status.key === b.status.key,
    order: true,
  };
};

function countTrueProperties(obj: { [key: string]: boolean }): number {
  let count = 0;
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] === true) {
      count++;
    }
  }
  return count;
}
